import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  AccessorFn,
} from '@tanstack/react-table';
import React from 'react';

import './BaseTable.scss';

// Extracted BaseTableCellContextType to explicitly define the return type for 'getValue',
// which defaults to 'any' in the library. Using 'unknown' provides better type safety compared to 'any',
export type BaseTableCellContextType = { getValue: () => unknown };

interface BaseTableProps<T> {
  data: T[];
  columnDefinitions: {
    minSize?: number;
    accessorKey: string | AccessorFn<T>;
    header: string;
    cell?: (context: BaseTableCellContextType) => React.ReactNode;
  }[];
  className?: string;
  maxHeight?: number;
  title?: string;
  pageSizeOptions?: number[];
  initialPageSize?: number;
}

const BaseTable = <T,>({
  data,
  className = '',
  columnDefinitions,
  title,
  maxHeight = 600,
  pageSizeOptions = [5, 10, 15],
  initialPageSize = 5,
}: BaseTableProps<T>): React.ReactElement => {
  const columnHelper = createColumnHelper<T>();

  const columns = columnDefinitions.map((col) =>
    columnHelper.accessor(col.accessorKey as AccessorFn<T>, {
      header: col.header,
      cell: col.cell ? col.cell : ({ getValue }) => <span>{String(getValue())}</span>, // Default cell rendering, ensures proper type conversion
      minSize: col.minSize,
    })
  );

  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: initialPageSize,
      },
    },
  });

  return (
    <div className={`base-table ${className}`}>
      <div className='base-table__container'>
        {title && <div className='base-table__title'>{title}</div>}
        <div className='base-table__table-scroll' style={{ maxHeight }}>
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        minWidth: header.column.columnDef.minSize ?? 50,
                        width: header.column.columnDef.size ?? 120,
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div className='base-table__pagination'>
            <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
              {'<<'}
            </button>
            <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
              {'<'}
            </button>
            <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              {'>'}
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              {'>>'}
            </button>
            <span>
              Page{' '}
              <strong>
                {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </strong>
            </span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            >
              {pageSizeOptions.map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseTable;
